import React from "react";

import { css, cx } from "@emotion/css";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { FontSizes } from "@/assets/fonts.ts";
import { Icons } from "@/assets/icons.ts";
import { useStyles } from "@/hooks/useStyles.ts";
import type { LibreViewClinicPatient } from "@/models/LibreViewModel.ts";
import { Queries } from "@/queries/Queries.ts";
import type { Theme } from "@/theme/CreateTheme.ts";
import { Button } from "@/uiKit/atoms/Button.tsx";
import { Card } from "@/uiKit/atoms/Card.tsx";
import { CardContent } from "@/uiKit/atoms/CardContent.tsx";
import { CardFooter } from "@/uiKit/atoms/CardFooter.tsx";
import { LibreviewInclusionFilters } from "@/uiKit/organisms/PatientSync/LibreviewGroupedInclusion/LibreviewGroupedInclusionFilters.tsx";
import { LibreviewGroupedInclusionTable } from "@/uiKit/organisms/PatientSync/LibreviewGroupedInclusion/LibreviewGroupedInclusionTable.tsx";
import { useLibreviewGroupedInclusionTable } from "@/uiKit/organisms/PatientSync/LibreviewGroupedInclusion/useLibreviewGroupedInclusionTable.ts";

export type LibreviewGroupedInclusionCardProps = {
  className?: string;
};

export const LibreviewGroupedInclusionCard: React.FC<LibreviewGroupedInclusionCardProps> = ({ className }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const styles = useStyles(makeStyles);
  const patients = Queries.patientSync.useLibreViewClinicPatients({});
  const [selectedPatients, setSelectedPatients] = React.useState<LibreViewClinicPatient[]>([]);
  const saveSelectedPatients = Queries.patientSync.useBulkCreateGroupedInclusions();
  const table = useLibreviewGroupedInclusionTable({
    patients,
    setRowSelection: setSelectedPatients,
  });

  return (
    <Card className={cx(styles.container, className)}>
      <div className={styles.bannerMenu}>
        <LibreviewInclusionFilters table={table} selectedCount={selectedPatients.length} />
        <Button
          variant="contained"
          data-action="sync.grouped-inclusion.create"
          startIcon={<Icons.userPlus className={styles.icon} />}
          onClick={() =>
            saveSelectedPatients.mutate(
              selectedPatients.map(p => ({
                firstName: p.firstName,
                lastName: p.lastName,
                email: p.email,
                phoneNumber: p.phoneNumber,
                libreviewId: p.libreviewId,
              })),
              {
                onSuccess: () => {
                  void navigate({ to: "/patient-sync/ongoing-imports" });
                },
              },
            )
          }
        >
          {t("patientSync.groupedInclusion.selectPatients", { count: selectedPatients.length })}
        </Button>
      </div>
      <CardContent>
        <LibreviewGroupedInclusionTable table={table} />
      </CardContent>
      <CardFooter />
    </Card>
  );
};

const makeStyles = (theme: Theme) => {
  const patientSyncNavigationHeaderHeight = `calc(2vh + ${theme.typography.h5.fontSize} * ${theme.typography.h5.lineHeight} + ${theme.spacing(16)} + ${theme.spacing(2)} + 2px)`; // top padding + title + title line height + navigation menu + gap + bottom border

  return {
    container: css({}),
    bannerMenu: css({
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      gap: theme.spacing(8),
      width: "100%",
      padding: theme.spacing(8),
      position: "sticky",
      top: patientSyncNavigationHeaderHeight,
      backgroundColor: theme.palette.common.white,
      zIndex: theme.zIndex.fab,
    }),
    icon: css({
      width: FontSizes.body,
      height: FontSizes.body,
    }),
  };
};
